"use client";

import {
  Badge,
  Button,
  cn,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  ArrowLeftRightIcon,
  BanknoteIcon,
  ChevronRightIcon,
  ClockIcon,
  ReceiptTextIcon,
  SendIcon,
  TrendingDownIcon,
  TrendingUpIcon,
  WalletIcon,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatPrice } from "@/shared/lib/format-price";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { SummaryCard } from "@/shared/ui/SummaryCard";
import { useWalletOverviewQuery } from "../api/queries";

const TX_TYPE_LABELS: Record<string, string> = {
  topup: "ເຕີມເງິນ",
  pay_order: "ຊຳລະອໍເດີ",
  pay_job: "ຊຳລະວຽກ",
  refund: "ຄືນເງິນ",
  payout: "ຈ່າຍອອກ",
  fee: "ຄ່າທຳນຽມ",
  adjust: "ປັບຍອດ",
};

const TX_STATUS_LABELS: Record<string, string> = {
  pending: "ລໍຖ້າ",
  posted: "ສຳເລັດ",
  failed: "ລົ້ມເຫຼວ",
  reversed: "ຍົກເລີກ",
};

const TX_STATUS_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  pending: "outline",
  posted: "secondary",
  failed: "destructive",
  reversed: "default",
};

const TX_REF_LABELS: Record<string, string> = {
  order: "ອໍເດີ",
  job: "ວຽກ",
  pos_sale: "ຂາຍ POS",
  manual: "ລະບົບ",
};

function formatTxDate(date: Date | string | null): string {
  if (!date) return "—";
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString("lo-LA", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function WalletOverviewPage() {
  const { data, ...result } = useWalletOverviewQuery();

  const d = data ?? {
    totalBalance: 0,
    currency: "LAK",
    outstandingReceivables: 0,
    outstandingPayables: 0,
    pendingPayouts: 0,
    pendingPayoutsCount: 0,
    recentTransactions: [],
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
            { label: "ພາບລວມ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              ພາບລວມກະເປົາເງິນ
            </h2>
            <p className="text-muted-foreground">
              ຍອດຄົງເຫຼືອ, ຍອດຄ້າງຮັບ/ຄ້າງຈ່າຍ ແລະ ການຈ່າຍເງິນທີ່ລໍຖ້າ
            </p>
          </div>
        </div>

        <QueryState result={result} isEmpty={false}>
          {/* KPI Cards */}
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryCard
              title="ຍອດຄົງເຫຼືອລວມ"
              icon={WalletIcon}
              color="orange"
              items={[
                {
                  label: "",
                  value: formatPrice(d.totalBalance, d.currency),
                },
              ]}
              subtitle={
                <span className="text-muted-foreground text-xs">
                  Platform Wallet
                </span>
              }
            />
            <SummaryCard
              title="ຍອດຄ້າງຮັບ"
              icon={TrendingUpIcon}
              color="success"
              items={[
                {
                  label: "",
                  value: formatPrice(d.outstandingReceivables, d.currency),
                },
              ]}
              subtitle={
                <span className="text-muted-foreground text-xs">
                  Outstanding Receivables
                </span>
              }
            />
            <SummaryCard
              title="ຍອດຄ້າງຈ່າຍ"
              icon={TrendingDownIcon}
              color="destructive"
              items={[
                {
                  label: "",
                  value: formatPrice(d.outstandingPayables, d.currency),
                },
              ]}
              subtitle={
                <span className="text-muted-foreground text-xs">
                  Outstanding Payables
                </span>
              }
            />
            <SummaryCard
              title="ການຈ່າຍເງິນລໍຖ້າ"
              icon={ClockIcon}
              color="primary"
              items={[
                {
                  label: "",
                  value: formatPrice(d.pendingPayouts, d.currency),
                },
              ]}
              subtitle={
                <span className="text-muted-foreground text-xs">
                  {d.pendingPayoutsCount} ລາຍການ Pending
                </span>
              }
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {/* Recent Transactions */}
            <div className="mb-6 rounded-xl border border-border bg-card p-5 lg:col-span-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex size-2 rounded-full bg-primary" />
                  <div>
                    <h2 className="font-bold text-lg tracking-tight">
                      ທຸລະກຳລ່າສຸດ
                    </h2>
                    <p className="text-muted-foreground text-sm">
                      5 ລາຍການຫຼ້າສຸດ
                    </p>
                  </div>
                </div>
                <Link to="/app/wallet/transactions">
                  <Button variant="outline" size="sm" className="gap-1.5">
                    ເບິ່ງປະຫວັດທັງໝົດ
                    <ChevronRightIcon className="size-4" />
                  </Button>
                </Link>
              </div>

              {d.recentTransactions.length > 0 ? (
                <div className="mt-4 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[160px]">ວັນທີ</TableHead>
                        <TableHead className="w-[130px]">ປະເພດ</TableHead>
                        <TableHead className="text-right">ຈຳນວນເງິນ</TableHead>
                        <TableHead className="w-[100px]">ສະຖານະ</TableHead>
                        <TableHead>ອ້າງອີງ</TableHead>
                        <TableHead>ໝາຍເຫດ</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {d.recentTransactions.map((tx) => {
                        const isCredit = ["topup", "refund"].includes(tx.type);
                        return (
                          <TableRow key={tx.id}>
                            <TableCell className="text-muted-foreground text-xs">
                              {formatTxDate(tx.createdAt)}
                            </TableCell>
                            <TableCell className="font-medium text-sm">
                              {TX_TYPE_LABELS[tx.type] ?? tx.type}
                            </TableCell>
                            <TableCell
                              className={cn(
                                "text-right font-semibold tabular-nums",
                                isCredit ? "text-success" : "text-foreground",
                              )}
                            >
                              {isCredit ? "+" : "−"}
                              {formatPrice(tx.amount, tx.currency)}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  TX_STATUS_VARIANT[tx.status] ?? "outline"
                                }
                                className="rounded-full px-2.5 py-0.5 text-xs"
                              >
                                {TX_STATUS_LABELS[tx.status] ?? tx.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-xs">
                              {tx.referenceType
                                ? `${TX_REF_LABELS[tx.referenceType] ?? tx.referenceType} #${tx.referenceId?.slice(0, 8) ?? "—"}`
                                : "—"}
                            </TableCell>
                            <TableCell className="max-w-[160px] truncate text-muted-foreground text-xs italic">
                              {tx.note ?? "—"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="mt-6 flex flex-col items-center gap-2 py-8 text-center">
                  <ReceiptTextIcon className="size-8 text-muted-foreground/50" />
                  <p className="text-muted-foreground">ຍັງບໍ່ມີທຸລະກຳໃນຂະນະນີ້</p>
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div className="flex flex-col gap-2">
              <QuickLinkCard
                icon={ArrowLeftRightIcon}
                title="ລາຍການທຸລະກຳ"
                description="ຄົ້ນຫາ ແລະ ກັ່ນກອງທຸລະກຳທັງໝົດ"
                to="/app/wallet/transactions"
              />
              <QuickLinkCard
                icon={SendIcon}
                title="ການຈ່າຍເງິນອອກ"
                description="ຈັດການຄິວການຈ່າຍເງິນໃຫ້ຮ້ານ/ຊ່າງ"
                to="/app/wallet/payouts"
              />
              <QuickLinkCard
                icon={BanknoteIcon}
                title="ເຕີມເງິນ / ຄືນເງິນ"
                description="ອະນຸມັດ top-up ແລະ ຈັດການ refund"
                to="/app/wallet/topup-refund"
              />
            </div>
          </div>
        </QueryState>
      </Main>
    </>
  );
}

function QuickLinkCard({
  icon: Icon,
  title,
  description,
  to,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  to: string;
}) {
  return (
    <Link
      to={to}
      className={cn(
        "group flex items-center gap-4 rounded-xl border border-border bg-card p-5",
        "transition-all hover:border-primary/30 hover:shadow-sm",
      )}
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-secondary/10">
        <Icon className="size-5 text-secondary" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm">{title}</p>
        <p className="text-muted-foreground text-xs">{description}</p>
      </div>
      <ChevronRightIcon className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}
